import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, MenuItem, Autocomplete } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, clientsAPI, employeesAPI } from '../../services/api';
import { NAVIGATION, CURRENCIES } from '../../constants';
import ProjectEmployeeManager from '../../components/projects/ProjectEmployeeManager';
import { Employee, ProjectFormData } from '../../types';

const schema = z.object({
  name: z.string().min(2),
  description: z.string().optional(),
  clientId: z.string().min(1),
  currency: z.string().min(1),
  startDate: z.string().min(1),
  endDate: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const AddProject: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      clientId: '',
      currency: CURRENCIES.EUR,
      startDate: '',
      endDate: '',
    }
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [clients, setClients] = useState<{ id: string; name: string; isActive?: boolean }[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [projectEmployees, setProjectEmployees] = useState<ProjectFormData['employees']>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsData, employeesData] = await Promise.all([
          clientsAPI.getAll(),
          employeesAPI.getActive()
        ]);
        setClients(clientsData.filter((client: { isActive?: boolean }) => client.isActive));
        setEmployees(employeesData);
      } catch (error) {
        setClients([]);
        setEmployees([]);
      }
    };
    fetchData();
  }, []);

  const onSubmit = async (data: FormData) => {
    setSuccess(''); setError('');
    try {
      const payload: any = {
        name: data.name,
        description: data.description || '', // Always include description, even if empty
        clientId: data.clientId,
        currency: data.currency,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
        employees: (projectEmployees || []).map(emp => ({
          ...emp,
          startDate: emp.startDate ? new Date(emp.startDate).toISOString() : undefined,
          endDate: emp.endDate ? new Date(emp.endDate).toISOString() : undefined,
        })),
      };
      if (data.endDate) payload.endDate = new Date(data.endDate).toISOString();
      await projectsAPI.create(payload);
      setSuccess('Project added successfully!');
      reset();
      navigate(NAVIGATION.PROJECTS);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add project');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Add New Project</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={clients.find((client) => client.id === watch('clientId')) || null}
              onChange={(_, newValue) => setValue('clientId', newValue?.id || '', { shouldValidate: true })}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  fullWidth
                  margin="normal"
                  error={!!errors.clientId}
                  helperText={errors.clientId?.message}
                />
              )}
            />
            <TextField label="Name" fullWidth margin="normal" {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
            <TextField label="Currency" select fullWidth margin="normal" {...register('currency')} error={!!errors.currency} helperText={errors.currency?.message} value={watch('currency') || CURRENCIES.EUR}>
              <MenuItem value={CURRENCIES.EUR}>EUR (€)</MenuItem>
              <MenuItem value={CURRENCIES.USD}>USD ($)</MenuItem>
              <MenuItem value={CURRENCIES.GBP}>GBP (£)</MenuItem>
              <MenuItem value={CURRENCIES.RSD}>RSD (din)</MenuItem>
              <MenuItem value={CURRENCIES.CHF}>CHF (CHF)</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Start Date" type="date" fullWidth margin="normal" {...register('startDate')} InputLabelProps={{ shrink: true }} error={!!errors.startDate} helperText={errors.startDate?.message} />
            <TextField label="End Date" type="date" fullWidth margin="normal" {...register('endDate')} InputLabelProps={{ shrink: true }} error={!!errors.endDate} helperText={errors.endDate?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField 
              label="Description" 
              fullWidth 
              margin="normal" 
              multiline
              rows={4}
              {...register('description')} 
              error={!!errors.description} 
              helperText={errors.description?.message} 
            />
          </Box>
        </Box>

        <ProjectEmployeeManager
          employees={employees}
          projectEmployees={projectEmployees}
          onEmployeesChange={setProjectEmployees}
          disabled={isSubmitting}
          projectCurrency={watch('currency') || CURRENCIES.EUR}
        />

        <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Project'}
        </Button>
      </form>
    </Box>
  );
};

export default AddProject; 